"use client";
import useTranslate from "@app/_hooks/useTranslate";
import type { Sort } from "types/common";

import ProductGrid from "@app/_components/ProductGrid/ProductGrid";
import type { LandingPageBody, LandingPageParams } from "@apptus/esales-api";
import { Button, Dialog } from "@components/ngg";
import { Pagination } from "@components/Pagination";
import ProductCard from "@components/ProductCard";
import { useMarket } from "@context/marketContext";
import type {
  BrandPageFragment,
  CampaignFragment,
  CategoryPageFragment,
  DesignerPageFragment,
} from "@server/gql/graphql";
import useCategory from "@server/hooks/useCategory";
import type { Attribute } from "@server/requests/product/fetchCategory";
import type {
  BasicCommerceData,
  Loop54Filter,
} from "@server/utils/parseLoop/types";
import type { PropsWithChildren, ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { LoopFacet } from "types/loop54";

import {
  PRODUCTS_PER_PAGE,
  TOTAL_PRODUCTS_WITH_EAGER_LOADING,
} from "@/config/products";
import { cn } from "@/lib/utils";
import type { MarketAwareSlug } from "@/utils/const";
import useSortingOptions from "@/utils/hooks/useSortingOptions";

import useEvents from "@app/_hooks/useEvents";
import ActiveFilters from "./activeFilters";
import Filter from "./Filter";
import SortBy from "./sortBy";

const CloseButton = (onClose: () => void, label: string) => (
  <button type="button" onClick={onClose} className="uppercase underline">
    {label}
  </button>
);

const ClearAll = (onClick: () => void, label: string) => (
  <button
    type="button"
    onClick={onClick}
    className="ml-auto mr-3 uppercase underline"
  >
    {label}
  </button>
);

type Props = PropsWithChildren<{
  attribute?: Attribute;
  initialData: any;
  defaultSort?: Sort;
  commerceData: any;
  basicCommerceData?: BasicCommerceData | null;
  pageData?:
    | BrandPageFragment
    | CampaignFragment
    | CategoryPageFragment
    | DesignerPageFragment
    | null;
  FeaturedProductsListComponent?: ReactNode;
  details: MarketAwareSlug;
  pageNumber?: number;
  applyCustomFilter?: Loop54Filter[];
  facets?: LoopFacet[] | undefined;
  // pageReference: string;
  // config?: LandingPageBody;
  // elevateFacets?: FacetParams;
  params: LandingPageParams;
  body?: LandingPageBody;
}>;

export default function Products(props: Props) {
  const t = useTranslate();
  const { viewList } = useEvents();
  const {
    applyCustomFilter,
    facets,
    initialData,
    defaultSort,
    commerceData,
    basicCommerceData,
    pageData,
    children,
    details,
    pageNumber = 1,
    attribute,
    // pageReference,
    // config,
    // elevateFacets,
    params,
    body,
  } = props;
  const { slug, cleanSlug } = details;

  const {
    formatter,
    market: { currency },
  } = useMarket().state;

  const { data, state, updateFilter, updateSort } = useCategory({
    initialData,
    attribute,
    facets,
    commerceData,
    applyCustomFilter,
    basicCommerceData,
    slug,
    pageNumber,
    params,
    body,
  });

  const resultsContainer = useRef<HTMLDivElement>(null);
  const [mobileFilterMenuOpen, setMobileFilterMenuOpen] = useState(false);

  const clearFilter = () => {
    if (!data?.filter) return;

    const filter = [...data.filter];
    const clearedFilter = filter.map((facet) => {
      const item = facet;
      item.values = [];
      return item;
    });
    updateFilter(clearedFilter);
  };

  const selectedFilters = useMemo(() => {
    if (!data?.filter) return 0;
    return data.filter.reduce((acc, facet) => {
      if (facet.type !== "checkbox") return acc;
      if (!facet?.values?.length) return acc;
      return acc + facet.values.length;
    }, 0);
  }, [data?.filter]);

  const sortingOptions = useSortingOptions();

  useEffect(() => {
    if (data?.items && pageData) {
      viewList(data?.items, pageData as any);
    }
  }, [data?.items, currency, pageData, viewList]);

  return (
    <>
      {children}
      <div
        ref={resultsContainer}
        className="flex scroll-mt-[var(--header-height)] justify-between border-y"
      >
        <div className="flex flex-1">
          <p className="flex h-10 flex-shrink-0 items-center pl-3">
            {t("nrOfProducts", {
              TOTAL: formatter.format(data?.count ?? 0),
            })}
          </p>
        </div>

        <button
          type="button"
          className="flex h-10 items-center px-3 tracking-wide lg:hidden"
          onClick={() => setMobileFilterMenuOpen(true)}
        >
          <span>{t("filter")}</span>
          {selectedFilters > 0 ? (
            <span className="ml-1 bg-black px-1 tabular-nums text-white">
              {selectedFilters}
            </span>
          ) : null}
        </button>

        {/* Mobile filter menu */}
        <Dialog
          open={mobileFilterMenuOpen}
          onClose={() => setMobileFilterMenuOpen(false)}
          title={t("filter")}
          dialogTransitionProps={{
            enter: "ease-out duration-150",
            enterFrom: "-translate-x-full",
            enterTo: "translate-x-0",
            leave: "ease-in duration-150",
            leaveFrom: "translate-x-0",
            leaveTo: "-translate-x-full",
          }}
          classNames={{
            backdrop: "bg-black/70",
            panel:
              "h-full flex flex-col rounded-none m-0 py-0 max-w-[326px] sm:max-w-[85%] text-left w-full mr-auto",
            panelHeader:
              "flex justify-between border-b border-grey-150 p-3 text-sm font-normal uppercase",
          }}
          SubtitleComponent={() => ClearAll(clearFilter, t("resetAll"))}
          CloseButton={() =>
            CloseButton(() => setMobileFilterMenuOpen(false), t("close"))
          }
        >
          {data?.filter?.length ? (
            <div className="grow overflow-y-auto">
              <Filter
                // initialFilter={initialData.filter}
                filter={data.filter}
                onChange={updateFilter}
              />
            </div>
          ) : null}
          <div className="border-t border-grey-150 px-3 py-4">
            <Button
              fullWidth
              className="btn-primary py-3 text-base"
              onClick={() => setMobileFilterMenuOpen(false)}
            >
              {t("showProducts")} ({data?.count})
            </Button>
          </div>
        </Dialog>

        <SortBy
          value={state?.sort}
          initialValue={defaultSort}
          options={sortingOptions}
          onChange={updateSort}
        />
      </div>

      <div className="grid scroll-mt-[var(--header-height)] grid-cols-12 items-start empty:hidden">
        {data?.filter.length ? (
          <div
            className={cn(
              "col-span-2 -mb-px hidden shrink-0 self-stretch lg:block",
            )}
          >
            <Filter
              slug={slug}
              filter={data.filter}
              onChange={updateFilter}
            />
          </div>
        ) : null}

        <div
          className="col-span-full lg:col-span-10"
          data-testid="product-category-grid"
        >
          <ActiveFilters
            data={data}
            updateFilter={updateFilter}
          />
          {data?.items?.length ? (
            <ProductGrid>
              {data?.items?.map?.((product, index) => (
                <ProductCard.Root
                  key={`${product.id}-${index}`}
                  product={product}
                  pageData={pageData}
                  isEager={index < TOTAL_PRODUCTS_WITH_EAGER_LOADING}
                >
                  <ProductCard.Link>
                    <ProductCard.Image>
                      <ProductCard.Badges />
                    </ProductCard.Image>
                    <ProductCard.Content />
                  </ProductCard.Link>
                </ProductCard.Root>
              ))}
            </ProductGrid>
          ) : null}
        </div>
      </div>

      <Pagination
        slug={cleanSlug}
        page={pageNumber}
        state={state}
        totalPages={data?.count ? Math.ceil(data.count / PRODUCTS_PER_PAGE) : 0}
      />
    </>
  );
}
